/**
 * Central application configuration.
 *
 * Every brandable / deployment-specific value lives here so that renaming the
 * product, changing bundle identifiers or pointing at a different backend is a
 * matter of environment variables rather than a find-and-replace across the
 * codebase.
 *
 * These defaults are duplicated by hand in apps/mobile/app.config.js, which
 * cannot import this package (Expo loads that file through a CommonJS require).
 * `scripts/check-brand-parity.mjs` fails `npm run verify` if the two drift.
 *
 * Resolution order for each value:
 *   1. an explicit override passed to `createAppConfig()`
 *   2. the matching environment variable
 *   3. the documented default below
 *
 * Reads from `process.env` (Node/Vite define), `import.meta.env` (Vite) and
 * Expo's `extra` block are all handled by the caller passing in a source map.
 */

const DEFAULTS = Object.freeze({
  // Identity
  appName: 'ChordBook Pro',
  appSlug: 'chordbook-pro',
  appTagline: 'Songs, chords and setlists for guitar and piano.',
  appDescription:
    'ChordBook Pro keeps your lyrics, chords, playlists and setlists in one place — ' +
    'on stage, in the practice room and offline.',

  // Platform identifiers
  androidPackageName: 'com.radanliev.chordbookpro',
  iosBundleIdentifier: 'com.radanliev.chordbookpro',
  iosAppStoreId: '',

  // URLs
  websiteUrl: 'https://chordbookpro.com',
  apiUrl: 'http://localhost:4000/api/v1',
  appStoreUrl: 'https://apps.apple.com/app/chordbook-pro/id000000000',
  googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.radanliev.chordbookpro',

  // Contact
  supportEmail: 'support@chordbookpro.com',
  copyrightEmail: 'copyright@chordbookpro.com',
  privacyEmail: 'privacy@chordbookpro.com',
  companyName: 'ChordBook Pro',

  // Branding
  logoUrl: '/brand/logo.svg',
  logoMarkUrl: '/brand/mark.svg',
  ogImageUrl: '/brand/og-image.png',

  // Subscription product identifiers.
  // These are placeholders — create the real products in App Store Connect,
  // Google Play Console and RevenueCat, then override via env. See
  // docs/revenuecat.md.
  // The live RevenueCat entitlement identifier — spaces and all. It is the
  // dashboard's *identifier* field, not its display name.
  entitlementId: 'ChordBook Pro Unlimited',
  offeringId: 'default',
  productIdIosMonthly: 'chordbook_pro_monthly',
  productIdIosAnnual: 'chordbook_pro_annual',
  productIdAndroidSubscription: 'chordbook_pro',
  basePlanIdAndroidMonthly: 'monthly',
  basePlanIdAndroidAnnual: 'annual',
  productIdWebMonthly: 'chordbook_pro_monthly_web',
  productIdWebAnnual: 'chordbook_pro_annual_web',

  // Fallback marketing pricing copy only. Paywalls and checkout MUST use live
  // product data from RevenueCat — never these strings.
  fallbackMonthlyPriceCopy: '$4.99 / month',
  fallbackAnnualPriceCopy: '$39.99 / year',
});

const ENV_KEYS = Object.freeze({
  appName: 'APP_NAME',
  appSlug: 'APP_SLUG',
  appTagline: 'APP_TAGLINE',
  appDescription: 'APP_DESCRIPTION',
  androidPackageName: 'ANDROID_PACKAGE_NAME',
  iosBundleIdentifier: 'IOS_BUNDLE_IDENTIFIER',
  iosAppStoreId: 'IOS_APP_STORE_ID',
  websiteUrl: 'WEBSITE_URL',
  apiUrl: 'API_URL',
  appStoreUrl: 'APP_STORE_URL',
  googlePlayUrl: 'GOOGLE_PLAY_URL',
  supportEmail: 'SUPPORT_EMAIL',
  copyrightEmail: 'COPYRIGHT_EMAIL',
  privacyEmail: 'PRIVACY_EMAIL',
  companyName: 'COMPANY_NAME',
  logoUrl: 'LOGO_URL',
  logoMarkUrl: 'LOGO_MARK_URL',
  ogImageUrl: 'OG_IMAGE_URL',
  entitlementId: 'REVENUECAT_ENTITLEMENT_ID',
  offeringId: 'REVENUECAT_OFFERING_ID',
  productIdIosMonthly: 'PRODUCT_ID_IOS_MONTHLY',
  productIdIosAnnual: 'PRODUCT_ID_IOS_ANNUAL',
  productIdAndroidSubscription: 'PRODUCT_ID_ANDROID_SUBSCRIPTION',
  basePlanIdAndroidMonthly: 'BASE_PLAN_ID_ANDROID_MONTHLY',
  basePlanIdAndroidAnnual: 'BASE_PLAN_ID_ANDROID_ANNUAL',
  productIdWebMonthly: 'PRODUCT_ID_WEB_MONTHLY',
  productIdWebAnnual: 'PRODUCT_ID_WEB_ANNUAL',
  fallbackMonthlyPriceCopy: 'FALLBACK_MONTHLY_PRICE_COPY',
  fallbackAnnualPriceCopy: 'FALLBACK_ANNUAL_PRICE_COPY',
});

/**
 * Builds an app config object.
 *
 * @param {object} [options]
 * @param {Record<string,string>} [options.env] environment source
 * @param {string} [options.prefix] prefix applied to env keys
 *   (`VITE_` for the web client, `EXPO_PUBLIC_` for mobile, '' for the API)
 * @param {Record<string,unknown>} [options.overrides] explicit overrides
 */
export function createAppConfig({ env = {}, prefix = '', overrides = {} } = {}) {
  const config = {};

  for (const [key, fallback] of Object.entries(DEFAULTS)) {
    if (overrides[key] !== undefined && overrides[key] !== null && overrides[key] !== '') {
      config[key] = overrides[key];
      continue;
    }
    const envKey = `${prefix}${ENV_KEYS[key]}`;
    const value = env[envKey];
    config[key] = value !== undefined && value !== null && value !== '' ? value : fallback;
  }

  // Derived, non-overridable conveniences.
  config.marketingUrls = Object.freeze({
    home: '/',
    features: '/features',
    guitar: '/guitar',
    piano: '/piano',
    pricing: '/pricing',
    faq: '/faq',
    contact: '/contact',
    privacy: '/privacy',
    terms: '/terms',
    copyright: '/copyright',
    accountDeletion: '/account-deletion',
    download: '/download',
  });

  return Object.freeze(config);
}

/** Default config resolved from bare `process.env`, used by the API. */
export const appConfig = createAppConfig({
  env: typeof process !== 'undefined' && process.env ? process.env : {},
});

export { DEFAULTS as APP_CONFIG_DEFAULTS, ENV_KEYS as APP_CONFIG_ENV_KEYS };
